import type { ListAssistantProposalsQueryParams } from "@packages/api-client/types/assistantProposalsController";
import { Action } from "@packages/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bot, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	scopedAssistantProposalsQueryOptions,
	scopedPendingAssistantProposalCountQueryOptions,
} from "@/api/scopedQueries";
import { ProposalCard } from "@/components/proposals/ProposalCard";
import { ProposalsExplainer } from "@/components/proposals/ProposalsExplainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasPermission } from "@/hooks/useAccessControl";
import { type FarmScope, useFarmScope } from "@/hooks/useFarmScope";
import { cn } from "@/utils/utils";

const STATUS_OPTIONS = ["PENDING", "EXECUTED", "REJECTED", "EXPIRED"] as const;
const PAGE_SIZE = 20;

/** Select/filter value: a proposal status literal or the "all" pseudo-filter. */
type StatusFilter =
	| NonNullable<ListAssistantProposalsQueryParams["status"]>
	| "all";

const searchSchema = z.object({
	// Default view is the actionable inbox; other statuses are opt-in filters.
	status: z.enum([...STATUS_OPTIONS, "all"]).optional(),
	farm: z.string().optional(),
	page: z.number().int().min(1).optional(),
});

/** URL search state → list query params (status defaulting to the inbox). */
function listFilters(
	status: StatusFilter | undefined,
	page: number | undefined,
) {
	const effectiveStatus = status ?? "PENDING";
	return {
		page: String(page ?? 1),
		limit: String(PAGE_SIZE),
		...(effectiveStatus !== "all" ? { status: effectiveStatus } : {}),
	};
}

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/pending-actions/",
)({
	validateSearch: searchSchema,
	loaderDeps: ({ search }) => ({
		status: search.status,
		farm: search.farm,
		page: search.page,
	}),
	loader: async ({ context, params, deps }) => {
		// `?farm=` is the org-wide scope param (see useEstablishment); mirror it
		// here the same way the home loader does.
		const scope: FarmScope = deps.farm
			? { kind: "single", establishmentId: deps.farm }
			: { kind: "all", establishmentIds: [] };
		// Warm the list the page reads. Non-reviewers get a 403 here — swallowed;
		// the component gates rendering on the permission before consuming it.
		context.queryClient
			.ensureQueryData(
				scopedAssistantProposalsQueryOptions(
					params.orgId,
					scope,
					listFilters(deps.status, deps.page),
				),
			)
			.catch(() => undefined);
	},
	component: PendingActionsPage,
});

function PendingActionsPage() {
	const { orgId } = Route.useParams();
	const { status, page } = Route.useSearch();
	const navigate = Route.useNavigate();
	const { t } = useTranslation();
	const scope = useFarmScope();
	const checkPermission = useHasPermission(orgId);

	const canReview = checkPermission(Action.REVIEW_AI_PROPOSALS);
	const statusFilter: StatusFilter = status ?? "PENDING";

	// Same query the home attention card and the sidebar badge read — no new
	// endpoint, no new cache key.
	const { data: counts } = useQuery({
		...scopedPendingAssistantProposalCountQueryOptions(orgId, scope),
		enabled: canReview,
		throwOnError: false,
	});
	const pending = counts?.pending ?? 0;

	if (!canReview) {
		return (
			<div data-testid="pending-actions-no-access" className="p-6 text-center">
				<h1 className="mb-2 text-xl font-bold">{t("proposals.page.title")}</h1>
				<p className="text-sm text-base-content/70">
					{t("proposals.page.noAccess")}
				</p>
			</div>
		);
	}

	const setStatusFilter = (value: string) =>
		navigate({
			search: (prev) => ({
				...prev,
				status: value as StatusFilter,
				page: undefined,
			}),
		});

	return (
		<div
			data-testid="pending-actions-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-4"
		>
			{/* `items-start` + the icon's own top offset: the h1 wraps to two lines at
			    390px, and centering the icon against the wrapped BLOCK floats it
			    between them instead of marking where the title starts. */}
			<div className="flex items-start gap-2.5">
				<Bot className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
				<div>
					<h1 data-testid="pending-actions-title" className="text-xl font-bold">
						{t("proposals.page.title")}
						{pending > 0 && (
							<span className="font-normal text-base-content/75">
								{" · "}
								{t("proposals.page.pendingCount", { count: pending })}
							</span>
						)}
					</h1>
					<p className="text-sm text-base-content/75">
						{t("proposals.page.subtitle")}
					</p>
				</div>
			</div>

			<StatusFilterBar
				value={statusFilter}
				pending={pending}
				onChange={setStatusFilter}
			/>

			{/* The trust claim AND the rules behind it, in one block — two panels
			    saying "nothing is written until you approve" are not twice the
			    trust, they are one message and one extra scroll. */}
			<ProposalsExplainer orgId={orgId} />

			<ProposalsList
				orgId={orgId}
				scope={scope}
				status={statusFilter}
				page={page}
				pending={pending}
			/>
		</div>
	);
}

/** The two filters that answer the question a reviewer actually arrives with. */
const PRIMARY_SEGMENTS: readonly StatusFilter[] = ["PENDING", "all"];

/**
 * The five statuses as labelled, visible segments rather than a closed dropdown.
 *
 * Five options sits right at the working-memory boundary, and for this audience
 * seeing the choices beats remembering them: a bare combobox hid both what the
 * alternatives were and which one was in force. Only PENDING carries a count —
 * it is the only one the API exposes without a new endpoint, and it is the only
 * one a reviewer acts on.
 *
 * On a phone all five wrap to two rows of chrome above the first decision, and
 * the default filter is right nearly every time — so the three history filters
 * fold behind one control there and stay laid out on any wider screen. A history
 * filter that IS in force never hides: a control whose own state is off-screen is
 * worse than the row it saved.
 */
function StatusFilterBar({
	value,
	pending,
	onChange,
}: {
	value: StatusFilter;
	pending: number;
	onChange: (next: StatusFilter) => void;
}) {
	const { t } = useTranslation();
	const segments: StatusFilter[] = [...STATUS_OPTIONS, "all"];
	const [showAll, setShowAll] = useState(false);
	const expanded = showAll || !PRIMARY_SEGMENTS.includes(value);

	return (
		<fieldset
			data-testid="pending-actions-filter"
			className="flex flex-wrap gap-1.5"
		>
			<legend className="sr-only">{t("proposals.filter.label")}</legend>
			{segments.map((segment) => {
				const selected = segment === value;
				return (
					<Button
						key={segment}
						type="button"
						variant={selected ? "default" : "outline"}
						size="touch"
						aria-pressed={selected}
						className={cn(
							!expanded &&
								!PRIMARY_SEGMENTS.includes(segment) &&
								"hidden sm:inline-flex",
						)}
						onClick={() => onChange(segment)}
					>
						{segment === "all"
							? t("proposals.filter.all")
							: t(`proposals.status.${segment}`)}
						{segment === "PENDING" && pending > 0 && (
							// Inverted on the selected segment: `outline` puts base-content
							// green ink on the solid primary green and measures 1.98:1 — the
							// least legible text on the page, on the one number the whole
							// triage phase exists to surface.
							<Badge
								variant={selected ? "default" : "warning"}
								className={cn(
									"ml-1.5 font-normal",
									selected &&
										"bg-primary-content text-primary hover:bg-primary-content",
								)}
							>
								{pending}
							</Badge>
						)}
					</Button>
				);
			})}
			{PRIMARY_SEGMENTS.includes(value) && (
				<Button
					type="button"
					variant="ghost"
					size="touch"
					className="sm:hidden"
					aria-expanded={showAll}
					onClick={() => setShowAll(!showAll)}
				>
					{t(showAll ? "proposals.filter.less" : "proposals.filter.more")}
				</Button>
			)}
		</fieldset>
	);
}

function ProposalsList({
	orgId,
	scope,
	status,
	page,
	pending,
}: {
	orgId: string;
	scope: FarmScope;
	status: StatusFilter;
	page: number | undefined;
	/** Pending across the whole scope — what an empty filtered view is hiding. */
	pending: number;
}) {
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data } = useSuspenseQuery(
		scopedAssistantProposalsQueryOptions(
			orgId,
			scope,
			listFilters(status, page),
		),
	);

	const rows = data.data;
	const pagination = data.pagination;

	const goToPage = (target: number) =>
		navigate({
			search: (prev) => ({
				...prev,
				page: target === 1 ? undefined : target,
			}),
		});

	if (rows.length === 0) {
		// A cleared inbox and a filter that matched nothing are different facts.
		// "Nada para revisar" read as a dead end on the default view, which is the
		// one a reviewer reaches when they have finished the work.
		const inbox = status === "PENDING";
		return (
			<div
				data-testid="pending-actions-empty"
				className="rounded-lg border border-base-300 bg-base-100 p-10 text-center"
			>
				<p className="font-medium">
					{t(
						inbox
							? "proposals.page.emptyInboxTitle"
							: "proposals.page.emptyTitle",
					)}
				</p>
				<p className="mt-1 text-sm text-base-content/75">
					{t(
						inbox
							? "proposals.page.emptyInboxDescription"
							: "proposals.page.emptyDescription",
					)}
				</p>
				{/* Make the FILTER visibly the cause: an empty screen with four
				    proposals one segment away reads as "there is nothing". */}
				{!inbox && pending > 0 && (
					<p className="mt-3 text-sm text-base-content/75">
						{t("proposals.page.emptyOtherPending", { count: pending })}
					</p>
				)}
			</div>
		);
	}

	return (
		<>
			<div className="space-y-4">
				{rows.map((proposal) => (
					<ProposalCard key={proposal.id} orgId={orgId} snapshot={proposal} />
				))}
			</div>

			{pagination.totalPages > 1 && (
				// Worded, not glyphs: `<` and `>` were 32px targets carrying no label,
				// the worst combination on this page.
				<nav
					aria-label={t("proposals.pager.label")}
					className="flex flex-wrap items-center justify-center gap-2"
				>
					<Button
						variant="outline"
						size="touch"
						disabled={!pagination.hasPreviousPage}
						onClick={() => goToPage(Math.max(1, (page ?? 1) - 1))}
					>
						<ChevronLeft className="mr-1.5 h-4 w-4" />
						{t("proposals.pager.previous")}
					</Button>
					<span aria-live="polite" className="text-sm text-base-content/75">
						{t("proposals.pager.position", {
							page: pagination.page,
							total: pagination.totalPages,
						})}
					</span>
					<Button
						variant="outline"
						size="touch"
						disabled={!pagination.hasNextPage}
						onClick={() => goToPage((page ?? 1) + 1)}
					>
						{t("proposals.pager.next")}
						<ChevronRight className="ml-1.5 h-4 w-4" />
					</Button>
				</nav>
			)}
		</>
	);
}
